const CACHE_PREFIX = 'vortex_cache_';
const DEFAULT_TTL = 7 * 24 * 60 * 60 * 1000;
const IDB_NAME = 'vortex_cache_db';
const IDB_STORE = 'entries';
const IDB_VERSION = 1;
const MAX_LOCAL_STORAGE_ENTRY = 4_500_000;
let _idbPromise = null;

function withTimeout(promise, ms, label) {
  let timer;
  return Promise.race([
    promise.finally(() => clearTimeout(timer)),
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(label)), ms);
    }),
  ]);
}

function openCacheDB() {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB no disponible'));
  }
  if (!_idbPromise) {
    _idbPromise = withTimeout(
      new Promise((resolve, reject) => {
        const request = indexedDB.open(IDB_NAME, IDB_VERSION);
        request.onupgradeneeded = () => {
          const database = request.result;
          if (!database.objectStoreNames.contains(IDB_STORE)) {
            database.createObjectStore(IDB_STORE);
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error || new Error('No se pudo abrir IndexedDB'));
        request.onblocked = () => reject(new Error('IndexedDB bloqueada'));
      }),
      2500,
      'Timeout abriendo IndexedDB'
    ).catch((error) => {
      _idbPromise = null;
      throw error;
    });
  }
  return _idbPromise;
}

export async function getIdbCachedData(key, ttl = DEFAULT_TTL) {
  try {
    const database = await openCacheDB();
    const entry = await withTimeout(
      new Promise((resolve, reject) => {
        const transaction = database.transaction(IDB_STORE, 'readonly');
        const request = transaction.objectStore(IDB_STORE).get(key);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error || new Error('Error leyendo IndexedDB'));
      }),
      2500,
      'Timeout leyendo IndexedDB'
    );
    if (!entry) return null;
    if (ttl > 0 && Date.now() - (entry.timestamp || 0) > ttl) {
      removeIdbCachedData(key).catch(() => {});
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

export async function setIdbCachedData(key, data, version = null) {
  try {
    const database = await openCacheDB();
    const entry = { data, version: version ?? null, timestamp: Date.now() };
    await withTimeout(
      new Promise((resolve, reject) => {
        const transaction = database.transaction(IDB_STORE, 'readwrite');
        transaction.objectStore(IDB_STORE).put(entry, key);
        transaction.oncomplete = () => resolve(true);
        transaction.onerror = () => reject(transaction.error || new Error('Error escribiendo IndexedDB'));
        transaction.onabort = () => reject(transaction.error || new Error('Transacción IndexedDB abortada'));
      }),
      8000,
      'Timeout escribiendo IndexedDB'
    );
    return true;
  } catch (error) {
    console.warn('[cache] No se pudo guardar en IndexedDB:', error?.message || error);
    return false;
  }
}

export async function removeIdbCachedData(key) {
  try {
    const database = await openCacheDB();
    await withTimeout(
      new Promise((resolve, reject) => {
        const transaction = database.transaction(IDB_STORE, 'readwrite');
        transaction.objectStore(IDB_STORE).delete(key);
        transaction.oncomplete = () => resolve(true);
        transaction.onerror = () => reject(transaction.error || new Error('Error borrando IndexedDB'));
        transaction.onabort = () => reject(transaction.error || new Error('Transacción IndexedDB abortada'));
      }),
      2500,
      'Timeout borrando IndexedDB'
    );
    return true;
  } catch {
    return false;
  }
}

export async function clearIdbCache() {
  try {
    const database = await openCacheDB();
    await withTimeout(
      new Promise((resolve, reject) => {
        const transaction = database.transaction(IDB_STORE, 'readwrite');
        transaction.objectStore(IDB_STORE).clear();
        transaction.oncomplete = () => resolve(true);
        transaction.onerror = () => reject(transaction.error || new Error('Error limpiando IndexedDB'));
        transaction.onabort = () => reject(transaction.error || new Error('Transacción IndexedDB abortada'));
      }),
      5000,
      'Timeout limpiando IndexedDB'
    );
    return true;
  } catch {
    return false;
  }
}

export function buildCacheKey(parts) {
  const key = Array.isArray(parts) ? parts.join('__') : String(parts);
  return CACHE_PREFIX + key;
}

export function getCachedData(key, ttl = DEFAULT_TTL) {
  try {
    const fullKey = key.startsWith(CACHE_PREFIX) ? key : buildCacheKey(key);
    const raw = localStorage.getItem(fullKey);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > ttl) {
      localStorage.removeItem(fullKey);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

export function getCachedDataWithVersion(key, ttl = DEFAULT_TTL) {
  try {
    const fullKey = key.startsWith(CACHE_PREFIX) ? key : buildCacheKey(key);
    const raw = localStorage.getItem(fullKey);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > ttl) {
      localStorage.removeItem(fullKey);
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

export function setCachedData(key, data, version = null) {
  const fullKey = key.startsWith(CACHE_PREFIX) ? key : buildCacheKey(key);
  const payload = { data, version: version || null, timestamp: Date.now() };
  let serialized;
  try {
    serialized = JSON.stringify(payload);
  } catch (error) {
    console.warn('[cache] No se pudo serializar la entrada:', error?.message || error);
    return false;
  }
  // Los volúmenes grandes viven en IndexedDB; evitar que saturen localStorage.
  if (serialized.length > MAX_LOCAL_STORAGE_ENTRY) {
    console.warn(`[cache] Entrada demasiado grande para localStorage (${Math.round(serialized.length / 1024)} KB): ${fullKey}`);
    return false;
  }
  try {
    localStorage.setItem(fullKey, serialized);
    return true;
  } catch (e) {
    if (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014) {
      // Liberar solo las entradas más antiguas, sin borrar todo el caché de la app.
      for (let attempt = 0; attempt < 3; attempt++) {
        const removed = evictOldestCacheEntries(CACHE_PREFIX, fullKey, 5);
        if (!removed) break;
        try {
          localStorage.setItem(fullKey, serialized);
          return true;
        } catch (retryError) {
          if (retryError.name !== 'QuotaExceededError' && retryError.code !== 22 && retryError.code !== 1014) break;
        }
      }
      console.warn('[cache] Sin espacio en localStorage tras liberar entradas antiguas');
    } else {
      console.warn('[cache] Error guardando en localStorage:', e?.message || e);
    }
    return false;
  }
}

export function removeCachedData(key) {
  try {
    const fullKey = key.startsWith(CACHE_PREFIX) ? key : buildCacheKey(key);
    localStorage.removeItem(fullKey);
  } catch {}
}

// Devuelve la entrada completa { data, version, timestamp } sin resolver a solo .data
export function getCachedEntry(key, ttl = DEFAULT_TTL) {
  try {
    const fullKey = key.startsWith(CACHE_PREFIX) ? key : buildCacheKey(key);
    const raw = localStorage.getItem(fullKey);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > ttl) {
      localStorage.removeItem(fullKey);
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

export function evictOldestCacheEntries(prefix, excludeKey = null, count = 5) {
  try {
    const entries = Object.keys(localStorage)
      .filter(k => k.startsWith(prefix) && k !== excludeKey)
      .map(k => {
        let timestamp = 0;
        try {
          timestamp = JSON.parse(localStorage.getItem(k))?.timestamp || 0;
        } catch {}
        return { key: k, timestamp };
      })
      .sort((a, b) => a.timestamp - b.timestamp);
    if (entries.length === 0) return false;
    entries.slice(0, count).forEach(({ key }) => localStorage.removeItem(key));
    return true;
  } catch {
    return false;
  }
}

export function removeCachePrefix(prefix) {
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith(prefix))
      .forEach(k => localStorage.removeItem(k));
  } catch {}
}

export function isCacheValid(entry, ttl = DEFAULT_TTL) {
  if (!entry || !entry.timestamp) return false;
  return Date.now() - entry.timestamp <= ttl;
}
